import {
  IEnvironmentVariables,
  IFetchUserInfo,
  IMembershipData,
  IUpstreamError,
  IUser,
  IUserInfo,
} from '../../../interfaces';
import { getEnv } from '../../environment';
import request from '../../services/request';
import durationUserInfo from '../../utils/durationUserInfo';
import { isPublicPage } from '../../utils/publicPage';
import { getFromStorage, removeFromStorage, setToStorage } from '../../utils/storage';
import userInfoKey from '../../utils/userInfoKey';
import { defaultUserInfo } from '../defaultContext';

const membershipStorageKey = `${userInfoKey}-membership`;

/**
 * Shape of the asado `GET /bff/v1/user/menu-data` envelope this provider now consumes (UnCurly /
 * FP-1363). It replaced the legacy universal-navigation `/menu/user-data` reader. The BFF returns a
 * `{ data, meta }` envelope and drops `planCode`, `lives` and `loggedIn`; `uuid` is retained (the
 * BFF sources it from the JWT `id` claim) because legacy consumers still read it. The adapter below
 * rebuilds the legacy `IUserInfo` so existing consumers keep working unchanged.
 */
interface IMenuDataUser {
  userId: number | null;
  uuid?: string;
  subscriptionState: 'ACTIVE' | 'CANCELED' | 'EXPIRED' | null;
  role: string | null;
  tier: string | null;
  subscriptionType: 'ANNUAL' | 'SEASONAL' | 'TRIAL' | null;
  firstName: string;
  lastName: string;
  shippingCountry: string;
  shippingZipCode: string;
  shippingState: string;
  shippingCity: string;
  email: string;
  subscriptionStartDate: number | null;
  subscriptionEndDate: number | null;
  phoneNumber: string;
}

interface IMenuDataEnvelope {
  data?: { user?: IMenuDataUser; avatar?: string; seasonCode?: string | null };
  errors?: IUpstreamError[];
}

/**
 * `planCode` is reconstructed purely as a backward-compat shim for the legacy Segment `plan_code`
 * trait and a few legacy plan checks — the BFF no longer emits it. Reverse of the BFF's
 * `subscriptionType` mapping. This is the single place planCode lives and the only thing to remove
 * once those legacy consumers migrate to `subscriptionType`.
 */
const PLAN_CODE_BY_SUBSCRIPTION_TYPE: Record<string, string> = {
  TRIAL: 'ffftrial',
  SEASONAL: 'fffvip',
  ANNUAL: 'fffvipa',
};

const adaptMenuData = (data: NonNullable<IMenuDataEnvelope['data']>): IUserInfo => {
  const user = data.user as IMenuDataUser;
  return {
    avatar: data.avatar ?? '',
    lives: [],
    // The endpoint is auth-required: an anonymous request 401s, which `request` rejects → the caller
    // falls back to `defaultUserInfo` (loggedIn:false). A successful payload therefore always means
    // the member is logged in.
    loggedIn: true,
    seasonCode: data.seasonCode ?? '',
    user: {
      userId: user.userId,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
      role: user.role ?? '',
      shippingCountry: user.shippingCountry,
      shippingState: user.shippingState,
      shippingZipCode: user.shippingZipCode,
      shippingCity: user.shippingCity,
      subscriptionEndDate: user.subscriptionEndDate ?? 0,
      subscriptionStartDate: user.subscriptionStartDate ?? 0,
      subscriptionState: (user.subscriptionState ?? '') as IUser['subscriptionState'],
      subscriptionType: (user.subscriptionType ?? '') as IUser['subscriptionType'],
      email: user.email,
      planCode: user.subscriptionType
        ? (PLAN_CODE_BY_SUBSCRIPTION_TYPE[user.subscriptionType] ?? '')
        : '',
      tier: user.tier ?? '',
      uuid: user.uuid ?? '',
    },
  };
};

const fetchUserInfo = async ({ env }: IFetchUserInfo): Promise<IUserInfo> => {
  const response = await request<IMenuDataEnvelope>({
    path: env.REACT_APP_USER_INFO_URL,
  });

  if (response && typeof response === 'object' && 'data' in response && response.data?.user) {
    const userInfo = adaptMenuData(response.data);
    // Surface the BFF's non-critical upstream errors so strict consumers can reject a degraded 200
    // (e.g. customize, which requires the subscriptions upstream) instead of rendering null
    // subscription facts as if real. Default consumers simply don't read this field.
    if (response.errors?.length) {
      userInfo.upstreamErrors = response.errors;
    }
    return userInfo;
  }

  return defaultUserInfo;
};

const fetchMembershipData = async ({
  env,
}: {
  env: IEnvironmentVariables;
}): Promise<IMembershipData | null> => {
  const cached = getMembershipFromStorage();

  try {
    const response = await request<IMembershipData>({
      path: env.REACT_APP_MEMBERSHIP_INFO_URL,
    });

    if (response && typeof response === 'object') {
      setToStorage({
        key: membershipStorageKey,
        value: response,
        durationInMs: durationUserInfo,
      });
      return response;
    }

    removeFromStorage({ key: membershipStorageKey });
    return null;
  } catch (err: unknown) {
    const error = err as Error;
    if (error.message === 'Unauthorized' || error.message === 'Forbidden') {
      removeFromStorage({ key: membershipStorageKey });
      return null;
    }

    return cached;
  }
};

const invalidateUserCache = () => {
  removeFromStorage({ key: userInfoKey });
  removeFromStorage({ key: membershipStorageKey });
};

/**
 * Bust the caller's **server-side** menu-data BFF cache (asado `DELETE
 * /bff/v1/user/menu-data/cache`). `invalidateUserCache` only clears the client
 * `sessionStorage` copy; the BFF keeps its own per-user (stale-while-revalidate)
 * entry, so a client refetch would just re-read the BFF's stale value. The bust is
 * keyed off the JWT cookie (a member can only bust their own entry). Body-less
 * DELETE (see other cache-bust callers re: `Content-Type`). Best-effort: a failed
 * bust only re-exposes the endpoint's short staleness, so it never throws.
 */
const invalidateUserInfoServerCache = async (): Promise<void> => {
  try {
    await fetch(`${getEnv().REACT_APP_USER_INFO_URL}/cache`, {
      method: 'DELETE',
      credentials: 'include',
      mode: 'cors',
      headers: { 'FFF-Auth': 'V1.1' },
    });
  } catch (error) {
    console.warn('Failed to invalidate menu-data BFF cache', error);
  }
};

/**
 * Force a fully fresh user-info read: bust the client `sessionStorage` copy AND the
 * server-side menu-data BFF cache, then cold-read from the BFF. Use this when a stale
 * read would drive a user-facing decision (e.g. the reactivation gate after a member
 * cancels in another app, whose cancel doesn't touch this app's client cache).
 */
const refreshUserInfo = async (): Promise<IUserInfo> => {
  invalidateUserCache();
  await invalidateUserInfoServerCache();
  return getUserInfo(true);
};

const getMembershipFromStorage = (): IMembershipData | null =>
  (getFromStorage({ key: membershipStorageKey }) as IMembershipData | undefined) ?? null;

const getMembershipData = async ({
  env,
}: {
  env: IEnvironmentVariables;
}): Promise<IMembershipData | null> => {
  const cached = getMembershipFromStorage();
  if (cached || isPublicPage) return cached;
  return fetchMembershipData({ env });
};

const getUserFromStorage = () => getFromStorage({ key: userInfoKey, defaultItem: defaultUserInfo });

// `throwOnAuthError` (opt-in, default off) rethrows the underlying auth error (401/403) instead of
// resolving `defaultUserInfo`, letting a caller distinguish "no/expired session" (→ redirect to
// login) from a transient upstream failure (still resolves `defaultUserInfo`). Off by default so
// every existing consumer keeps the swallow-all behavior.
const getUserInfo = async (
  bypassPublicPageCheck = false,
  throwOnAuthError = false,
): Promise<IUserInfo> => {
  // check if on public page and prevent user info fetch
  if (!bypassPublicPageCheck && isPublicPage) return Promise.resolve(defaultUserInfo);

  const userFromSTG = getFromStorage({ key: userInfoKey });
  if (userFromSTG) {
    return userFromSTG;
  }
  removeFromStorage({ key: userInfoKey });
  try {
    const requestUserInfo = await fetchUserInfo({ env: getEnv() });

    setToStorage({
      key: userInfoKey,
      value: requestUserInfo,
      durationInMs: durationUserInfo,
    });
    return requestUserInfo;
  } catch (err: unknown) {
    const error = err as Error;
    if (error.message === 'Unauthorized' || error.message === 'Forbidden') {
      removeFromStorage({ key: userInfoKey });
      removeFromStorage({ key: membershipStorageKey });
      if (throwOnAuthError) throw error;
    } else {
      console.log(err, error.message);
    }
  }
  return Promise.resolve(defaultUserInfo);
};

export {
  fetchMembershipData,
  fetchUserInfo,
  getMembershipData,
  getMembershipFromStorage,
  invalidateUserCache,
  invalidateUserInfoServerCache,
  refreshUserInfo,
  getUserFromStorage,
  getUserInfo,
};
